import { formatPhone } from "shared/utils";
import {
  ApplicantType,
  ApplicationStatus,
  ApplicationFeeStatus,
  ApplicantStatus,
  Invitee
} from "shared/types/application";
import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";
import moment from "moment";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { ApplicationActionsMenu } from "shared/components/ApplicationActionsMenu";
import { ApplicationActionsMenuVariant } from "shared/components/ApplicationActionsMenu/types";
import { Button, ButtonSize } from "shared/components";
import { useContext, useEffect, useRef } from "react";
import { PrintAllContext } from "views/applications-id-report/providers/PrintProvider";
import { useLocation } from "react-router-dom";
import { Redirect } from "shared/components/Redirect";
import { getFullName } from "shared/utils/get-full-name";
import { Show } from "shared/components";
import { useAuthContext } from "providers/Auth";
import { Applicant } from "types/application";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  File02Icon,
  Mail02Icon,
  SmartPhone01Icon,
  ViewOffSlashIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { ApplyDiscountModal } from "views/discounts/components/ApplyDiscountModal";
import {
  useApplyDiscountToApplicant,
  useDiscounts,
  useRemoveDiscountFromApplicant
} from "views/discounts/hooks/useDiscounts";
import { DiscountCode } from "views/discounts/types";
import { DiscountStatus } from "views/discounts/components/DiscountStatus";
import { useState } from "react";
import { useDownloadReportPdf } from "../../hooks";
import { getPrintApplicationTooltip } from "../../helper";
import { VerificationNames, VerificationStatus } from "shared/types";
import {
  ArrowDown01Icon,
  ArrowUp01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { ResumeTooltip } from "shared/app/OnHoldResume/ResumeTooltip";
import { FeeStatusDisplay } from "./components/FeeStatusDisplay";
import { ApplicationFee } from "./components/ApplicationFee";
import { getApplicantRole } from "utils/getApplicantRole";

interface ApplicantCardSliceProps {
  applicant?: Applicant;
  applicationStatus: ApplicationStatus;
  verificationIds: number[];
  templateId?: string;
  allowCoSigner: boolean;
  allowOccupant: boolean;
  pendingMagicLink?: Invitee;
  onCancelClick?: (applicantId: string) => void;
  onUncancelClick?: (applicantId: string) => void;
  onChangeRoleClick?: (applicant: any) => void;
  onRefundClick?: (applicantId: string) => void;
  onResendInviteClick?: (invite: Invitee) => void;
}

export const ApplicantCardSlice = ({
  applicant,
  applicationStatus,
  verificationIds,
  templateId,
  allowCoSigner,
  allowOccupant,
  pendingMagicLink,
  onCancelClick,
  onUncancelClick,
  onChangeRoleClick,
  onRefundClick,
  onResendInviteClick
}: ApplicantCardSliceProps) => {
  const { setDocumentPrintingState, setDocuments } =
    useContext(PrintAllContext);
  const location = useLocation();
  // const navigate = useNavigate();

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const { type, permissions } = useAuthContext();
  const { adminPrivileges } = type;
  const [discountModal, setDiscountModal] = useState<{
    email: string;
    id: number;
  } | null>(null);

  const applyDiscountMutation = useApplyDiscountToApplicant();
  const removeDiscountMutation = useRemoveDiscountFromApplicant();
  const { data: activeDiscounts = [] } = useDiscounts({
    active: true,
    restriction_type: "template",
    template_id: templateId,
    enabled: !!templateId
  });

  const handleApplyDiscount = (
    discount:
      | DiscountCode
      | {
          isCustom: true;
          code: string;
          discount_type: string;
          discount_amount: number;
          end_date?: string;
        }
  ) => {
    if (!applicant || !discountModal) return;

    const applicationId = applicant.application_id;
    const applicantId = discountModal.id;

    const discountData =
      "isCustom" in discount
        ? {
            discount_code: discount.code,
            custom_discount_type: discount.discount_type,
            custom_discount_amount: discount.discount_amount,
            custom_end_date: discount.end_date
          }
        : { discount_code: discount.code };

    const isApplicantEntity = "verifications" in applicant;

    applyDiscountMutation.mutate({
      applicationId,
      applicantId,
      discountData,
      isApplicantEntity
    });

    setDiscountModal(null);
  };

  const handleCancelDiscount = (applicantId: number) => {
    if (!applicant) return;

    const applicationId = applicant.application_id;

    const isApplicantEntity = "verifications" in applicant;

    removeDiscountMutation.mutate({
      applicationId,
      applicantId,
      isApplicantEntity
    });
  };

  const { id, application_id } = applicant || {};

  const { startPolling, isPolling } = useDownloadReportPdf({
    applicationId: String(application_id || 0),
    applicantId: String(id || 0)
  });

  useEffect(() => {
    if (location.pathname.includes("/print")) {
      setDocuments({});
      setDocumentPrintingState(
        verificationIds.map((id) => {
          return { status: "loading", reportId: id };
        })
      );
    }
  }, []);

  const printApplicationTooltip = getPrintApplicationTooltip({
    isPolling,
    startedAt: applicant?.application_started_at
  });

  const applicationFee = applicant?.fee_statuses?.find(
    (feeStatus) => feeStatus.fee_type === "application_fee"
  );
  const administrationFee = applicant?.fee_statuses?.find(
    (feeStatus) => feeStatus.fee_type === "administration_fee"
  );
  const holdingFee = applicant?.fee_statuses?.find(
    (feeStatus) => feeStatus.fee_type === "holding_fee"
  );

  return (
    <div className="rounded-lg border border-alpha-100 bg-white p-3 md:p-6 shadow-xs">
      {applicant ? (
        <>
          <div className="mb-4 flex justify-between gap-2 border-b border-alpha-40 pb-4">
            <div className="flex gap-4 w-full md:flex-nowrap flex-wrap">
              <div>
                <div className="flex items-center">
                  <h1 className="text-xl font-bold text-content-1000 mr-4">
                    {getFullName({
                      firstName: applicant.first_name,
                      lastName: applicant.last_name,
                      middleName: applicant.middle_name,
                      short: true
                    })}
                  </h1>
                  <ApplicationStatusBadge
                    status={applicant.application_status}
                    type="applicant"
                    isAutoCanceled={applicant.canceled_by_auto_cancel}
                  />
                </div>
                <p className="text-sm text-content-400 mt-2">
                  {getApplicantRole(applicant.role, applicant.is_primary)}
                </p>
              </div>

              <div className="flex gap-4 items-center ml-auto">
                <div className="print:hidden flex items-center">
                  <ApplicationActionsMenu
                    variant={ApplicationActionsMenuVariant.Detail}
                    borderLess={false}
                    applicant={applicant}
                    pendingMagicLink={pendingMagicLink}
                    applicationStatus={applicationStatus}
                    hasAdminPrivileges={adminPrivileges}
                    permissions={{
                      canChangeApplicantRole:
                        permissions.canChangeApplicantRole,
                      canCancelApplications: permissions.canCancelApplications
                    }}
                    allowCoSigner={allowCoSigner}
                    allowOccupant={allowOccupant}
                    onCancelClick={onCancelClick}
                    onUncancelClick={onUncancelClick}
                    onChangeRoleClick={onChangeRoleClick}
                    onRefundClick={onRefundClick}
                    onResendInviteClick={onResendInviteClick}
                  />
                </div>

                {isPolling ? (
                  <Button
                    disabled
                    tooltip={printApplicationTooltip}
                    loading={isPolling}
                    className="print:hidden md:flex hidden min-w-[125px]"
                    size={ButtonSize.SM}
                  >
                    Processing...
                  </Button>
                ) : (
                  <ContextMenuPopover
                    placement="bottom-end"
                    disabled={
                      !applicant.application_started_at ||
                      (applicant.is_manual &&
                        applicant.verifications.find(
                          (v) => v.name === VerificationNames.RentalApplication
                        )?.status !== VerificationStatus.Finished) ||
                      applicant.application_status === ApplicantStatus.OnHold
                    }
                    cbTrigger={(open) => (
                      <Button
                        HugeIcon={open ? ArrowUp01Icon : ArrowDown01Icon}
                        size={ButtonSize.SM}
                        disabled={
                          !applicant.application_started_at ||
                          (applicant.is_manual &&
                            applicant.verifications.find(
                              (v) =>
                                v.name === VerificationNames.RentalApplication
                            )?.status !== VerificationStatus.Finished) ||
                          applicant.application_status ===
                            ApplicantStatus.OnHold
                        }
                        tooltipDelay={250}
                        tooltip={
                          applicant.application_status ===
                          ApplicantStatus.OnHold ? (
                            <ResumeTooltip
                              applicationId={applicant.application_id}
                            />
                          ) : undefined
                        }
                      >
                        Print application
                      </Button>
                    )}
                    actions={[
                      {
                        title: (
                          <div className="flex gap-4 items-center w-[442px] p-2">
                            <HugeiconsIcon
                              icon={File02Icon}
                              size={32}
                              stroke="1px"
                              className="text-content-1000"
                            />

                            <div>
                              <h2 className="text-base mb-1 text-content-1000 font-medium">
                                Full version
                              </h2>
                              <p className="text-sm text-content-400">
                                Default version that includes all data
                              </p>
                            </div>
                          </div>
                        ),
                        onClick: () => {
                          startPolling({
                            include_redacted_report: false,
                            include_government_id_report: false
                          });
                        }
                      },
                      {
                        title: (
                          <div className="flex gap-4 items-center w-[442px] p-2">
                            <HugeiconsIcon
                              icon={ViewOffSlashIcon}
                              size={32}
                              stroke="1px"
                              className="text-content-1000"
                            />

                            <div>
                              <h2 className="text-base mb-1 text-content-1000 font-medium">
                                Redacted version
                              </h2>
                              <p className="text-sm text-content-400">
                                Excludes personal identifying details for
                                privacy
                              </p>
                            </div>
                          </div>
                        ),
                        onClick: () => {
                          startPolling({
                            include_redacted_report: true,
                            include_government_id_report: false
                          });
                        }
                      }
                    ]}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="mb-4 flex justify-between gap-2 border-b border-alpha-40 pb-4">
            <div className="flex items-end gap-8 md:flex-nowrap flex-wrap">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-sm font-medium text-content-1000">
                  <HugeiconsIcon
                    icon={SmartPhone01Icon}
                    size="16"
                    className="text-content-200"
                  />
                  {applicant.phone
                    ? formatPhone(applicant.phone)
                    : "No US number provided"}
                </div>

                <div className="flex items-center gap-2 text-sm font-medium text-content-1000">
                  <HugeiconsIcon
                    icon={Mail02Icon}
                    size="16"
                    className="text-content-200"
                  />
                  {applicant.email}
                </div>
              </div>
            </div>
            <div className="flex gap-10">
              <Show if={applicant.application_started_at}>
                <div>
                  <p className="text-xs text-content-400 text-right mb-2">
                    Start date
                  </p>
                  <p className="text-sm font-normal text-content-1000">
                    {moment(applicant.application_started_at).format(
                      "MMM DD, YYYY [at] h:mm A"
                    )}
                  </p>
                </div>
              </Show>

              <Show if={applicant.application_submitted_at}>
                <div>
                  <p className="text-xs text-content-400 text-right mb-2">
                    Submission date
                  </p>
                  <p className="text-sm font-normal text-content-1000">
                    {moment(applicant.application_submitted_at).format(
                      "MMM DD, YYYY [at] h:mm A"
                    )}
                  </p>
                </div>
              </Show>

              <Show
                if={
                  applicant.application_status === ApplicantStatus.Canceled &&
                  applicant.application_canceled_at
                }
              >
                <div>
                  <p className="text-xs text-content-400 text-right mb-2">
                    Cancel date
                  </p>
                  <p className="text-sm font-normal text-content-1000">
                    {moment(applicant.application_canceled_at).format(
                      "MMM DD, YYYY [at] h:mm A"
                    )}
                  </p>
                </div>
              </Show>
            </div>
          </div>
          <div className="flex items-end justify-between">
            <div className="flex gap-10">
              <ApplicationFee
                applicant={applicant}
                applicationFee={applicationFee}
              />
              <div className="space-y-2 text-sm">
                <p className="text-xs text-content-300 font-normal">Discount</p>
                <DiscountStatus
                  applicant={{
                    id: applicant.id,
                    application_fee_status:
                      applicant.application_fee_status || null,
                    application_status: applicant.application_status,
                    discount_info: applicant.discount_info
                  }}
                  onApplyDiscountClick={() => {
                    setDiscountModal({
                      email: applicant.email,
                      id: applicant.id
                    });
                  }}
                  onCancelDiscountClick={handleCancelDiscount}
                  isActiveDiscounts={!!activeDiscounts.length}
                />
              </div>
              {(administrationFee || holdingFee) && (
                <div aria-hidden className="border-r border-alpha-40" />
              )}
              {administrationFee && (
                <FeeStatusDisplay fee={administrationFee} />
              )}
              {holdingFee && <FeeStatusDisplay fee={holdingFee} />}
            </div>
          </div>
        </>
      ) : (
        <Redirect href="/applications" />
      )}

      {discountModal ? (
        <ApplyDiscountModal
          opened={true}
          onClose={() => setDiscountModal(null)}
          applicantEmail={discountModal.email}
          activeDiscounts={activeDiscounts}
          onApplyDiscount={handleApplyDiscount}
        />
      ) : null}

      <iframe className="hidden" ref={iframeRef} />
    </div>
  );
};
